// 工事情報取得(キャッシュ)ページ。mock には無い web 専用機能のため、UI は mock 系の
// 新イディオム(見出し+件数Chip / info Alert / Paper+素Table / 60%幅ボタン)に合わせる。
// 工事は Work マスタに一本化済み(工事は担当者を持たないため「登録者」列は無い)。
import { Alert, Box, Checkbox, Chip, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { useState, useEffect } from "react";
import LoadingDialog from "../../../../components/dialog/LoadingDialog";
import StyledButton from "../../../../components/StyledButton";
import Time from "../../../../util/Time";
import useAsyncUpdate from "../../../../util/useAsyncUpdate";
import { PageContext } from "../../PageContext";
import { ColumnLinkBuilder } from "../../../../components/ColumnLinkBuilder";
import { ContentLoading } from "../../../../components/Loading";

// 工事(Work)の一覧表示名。short_name を優先し、空なら official_name を使う。
function workLabel(work: { short_name: string; official_name: string }): string {
  return work.short_name || work.official_name;
}

export default function CachePage({ pageContext }: { pageContext: PageContext } ) {
  const [allWorks, updateAllWorks] = useAsyncUpdate(() => pageContext.api.cache.getCacheAbleWorks());
  const [isOnline, setOnline] = useState(pageContext.api.isOnline);
  const [targetWorkIds, setTargetWorkIds] = useState<number[]>([]);
  const [showLoadingDialog, setShowLoadingDialog] = useState(false);

  useEffect(() => {
    const dispose = pageContext.api.subscribeOnlineState("CachePageUI", () => {
      updateAllWorks();
      setOnline(pageContext.api.isOnline);
    });
    return dispose;
  }, []);

  const works = allWorks ?? [];
  const allSelected = works.length > 0 && targetWorkIds.length === works.length;
  const someSelected = targetWorkIds.length > 0 && !allSelected;

  const toggleAll = (checked: boolean) => {
    if (checked) {
      setTargetWorkIds(works.map((w) => w.id));
    } else {
      setTargetWorkIds([]);
    }
  };

  const toggleOne = (id: number, checked: boolean) => {
    setTargetWorkIds((prev) => {
      if (checked) {
        return [...prev, id];
      }
      return prev.filter((p) => p !== id);
    });
  };

  return (
    <Stack p={3} maxWidth={1200} mx="auto" gap={2}>
      <Stack direction="row" alignItems="center" gap={1}>
        <Typography variant="h5" fontWeight="bold">工事情報取得</Typography>
        <Chip size="small" color="info" label={`${works.length} 工事`} />
        {targetWorkIds.length > 0 && <Chip size="small" color="primary" label={`選択中 ${targetWorkIds.length} 件`} />}
      </Stack>

      <Alert severity="info">
        オフライン環境でも工事データを利用できるよう、選択した工事の関連データを端末に取得(キャッシュ)します。現場へ出る前に、対象の工事を選択して取得してください。
      </Alert>

      {!isOnline && (
        <Alert severity="warning">現在オフラインです。通信できる場所に移動して再度試してください。</Alert>
      )}

      {isOnline && (
        <>
          <Paper variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={allSelected}
                      indeterminate={someSelected}
                      onChange={(e) => toggleAll(e.target.checked)}
                      inputProps={{ "aria-label": "すべて選択" }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>工事</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>名前</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>登録日</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {allWorks == null && (
                  <TableRow>
                    <TableCell colSpan={4} align="center"><ContentLoading minHeight={120} /></TableCell>
                  </TableRow>
                )}
                {allWorks != null && works.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ color: "text.secondary", py: 4 }}>キャッシュ可能な工事がありません</TableCell>
                  </TableRow>
                )}
                {works.map((w) => (
                  <TableRow key={w.id} hover>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={targetWorkIds.includes(w.id)}
                        onChange={(e) => toggleOne(w.id, e.target.checked)}
                        inputProps={{ "aria-label": `${workLabel(w)} を選択` }}
                      />
                    </TableCell>
                    <TableCell>{ColumnLinkBuilder(`/console/master-view/work/${w.id}/`, `工事 ${w.id}`)}</TableCell>
                    <TableCell>{workLabel(w)}</TableCell>
                    <TableCell>{new Time(w.createdAt).Format("YYYY/MM/dd HH:mm:ss")}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>

          <Box textAlign="center">
            <StyledButton buttonStyle="primary" onClick={() => setShowLoadingDialog(true)} sx={{ py: 1.5, width: "60%" }} disabled={targetWorkIds.length === 0}>
              <Typography fontWeight="bold">{targetWorkIds.length === 0 ? "工事を選択してください" : `選択した ${targetWorkIds.length} 件のキャッシュを取得`}</Typography>
            </StyledButton>
          </Box>
        </>
      )}

      <LoadingDialog opened={showLoadingDialog} onClose={() => setShowLoadingDialog(false)} promise={async () => {
        for (const targetId of targetWorkIds) {
          await pageContext.api.cache.getCacheFromWorkIds({ userId: pageContext.api.currentUser.id, targetIds: [targetId] })
        }
      }}/>
    </Stack>
  );
}
